import React, {

  useEffect,

  useState,

} from "react";

import {

  useLocation,

  useNavigate,

  useParams,

} from "react-router-dom";

import {

  useDispatch,
  useSelector,

} from "react-redux";

import {

  ArrowLeft,

  ArrowRight,

  CheckCircle2,

  CreditCard,

  Leaf,

  LockKeyhole,

  ShieldCheck,

  ShoppingBag,

  Smartphone,

} from "lucide-react";

import {

  createRazorpayOrder,

  verifyRazorpayPayment,

} from "../../actions/paymentActions";

import {

  useCart,

} from "../../components/CartContext";

import { useRestaurantSession } from "../../components/RestaurantSessionProvider";
import "./css/PaymentScreen.css";

/* =========================================================

   RAZORPAY SCRIPT

\========================================================= */

const RAZORPAY_SCRIPT_URL =

  "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {

  return new Promise(

    (resolve) => {

      /* =================================================

         ALREADY LOADED

      \================================================= */

      if (window.Razorpay) {

        resolve(true);

        return;

      }

      /* =================================================

         SCRIPT ALREADY EXISTS

      \================================================= */

      const existingScript =

        document.querySelector(

          `script[src="${RAZORPAY_SCRIPT_URL}"]`

        );

      if (existingScript) {

        existingScript.addEventListener(

          "load",

          () => resolve(true)

        );

        existingScript.addEventListener(

          "error",

          () => resolve(false)

        );

        return;

      }

      /* =================================================

         CREATE SCRIPT

      \================================================= */

      const script =

        document.createElement(

          "script"

        );

      script.src =

        RAZORPAY_SCRIPT_URL;

      script.async = true;

      script.onload = () => {

        resolve(

          Boolean(

            window.Razorpay

          )

        );

      };

      script.onerror = () => {

        resolve(false);

      };

      document.body.appendChild(

        script

      );

    }

  );

}

/* =========================================================

   PAYMENT SCREEN

\========================================================= */

function PaymentScreen() {

  const navigate =

    useNavigate();

  const location =

    useLocation();

  const dispatch =

    useDispatch();

  const {

    restaurantId,

    tableId,

  } = useParams();

  /* =====================================================

     CART

  \===================================================== */

  const {

    clearCart,

  } = useCart();
  const { sessionToken } = useRestaurantSession();
  const createdOrder = useSelector((state) => state.order?.order);

  /* =====================================================

     ORDER DATA

  \===================================================== */

  const orderId = location.state?.orderId || createdOrder?.id || createdOrder?.order_id;
  const subtotal = location.state?.subtotal ?? createdOrder?.subtotal ?? 0;
  const tax = location.state?.tax ?? createdOrder?.tax ?? 0;
  const serviceCharge = location.state?.serviceCharge ?? createdOrder?.service_charge ?? 0;
  const total = location.state?.total ?? createdOrder?.total ?? 0;

  /* =====================================================

     STATE

  \===================================================== */

  const [

    loading,

    setLoading,

  ] = useState(false);

  const [

    paymentError,

    setPaymentError,

  ] = useState("");

  /* =====================================================

     HOME PATH

  \===================================================== */

  const homePath =

    `/${restaurantId}/table/${tableId}`;

  /* =====================================================

     FORMAT PRICE

  \===================================================== */

  const formatPrice = (

    value

  ) => {

    return Number(

      value || 0

    ).toFixed(2);

  };

  /* =====================================================

     CHECK ORDER

  \===================================================== */

  useEffect(() => {

    if (!orderId) {

      setPaymentError(

        "Order information is missing. Please create your order again."

      );

    }

  }, [

    orderId,

  ]);

  /* =====================================================

     HANDLE PAYMENT

  \===================================================== */

  const handlePayment =

    async () => {

      /* -------------------------------------------------

         CHECK ORDER

      \------------------------------------------------- */

      if (!orderId) {

        setPaymentError(

          "Order information is missing."

        );

        return;

      }

      try {

        setLoading(true);

        setPaymentError("");

        /* =================================================

           LOAD RAZORPAY

        \================================================= */

        const razorpayLoaded =

          await loadRazorpayScript();

        if (!razorpayLoaded) {

          throw new Error(

            "Razorpay Checkout could not be loaded. Please check your internet connection and try again."

          );

        }

        /* =================================================

           CREATE RAZORPAY ORDER

        \================================================= */

        const paymentOrder =

          await dispatch(

            createRazorpayOrder(

              restaurantId,

              orderId,
            sessionToken

            )

          );

        console.log(

          "RAZORPAY CREATE RESPONSE:",

          paymentOrder

        );

        /* =================================================

           VALIDATE RAZORPAY ORDER

        \================================================= */

        if (

          !paymentOrder?.razorpay_order_id

        ) {

          throw new Error(

            "Razorpay order ID was not returned by the server."

          );

        }

        if (

          !paymentOrder?.amount

        ) {

          throw new Error(

            "Payment amount was not returned by the server."

          );

        }

        /* =================================================

           RAZORPAY OPTIONS

        \================================================= */

        const options = {

          /* ---------------------------------------------

             KEY

          \--------------------------------------------- */

          key:

            paymentOrder.key_id ||

            paymentOrder.razorpay_key_id ||

            paymentOrder.key,

          /* ---------------------------------------------

             AMOUNT

             Amount comes from backend.

             Razorpay expects amount in smallest currency

             unit such as paise for INR.

          \--------------------------------------------- */

          amount:

            Number(

              paymentOrder.amount

            ),

          /* ---------------------------------------------

             CURRENCY

          \--------------------------------------------- */

          currency:

            paymentOrder.currency ||

            "INR",

          /* ---------------------------------------------

             RESTAURANT

          \--------------------------------------------- */

          name:

            paymentOrder.restaurant_name ||

            "YUM!",

          /* ---------------------------------------------

             DESCRIPTION

          \--------------------------------------------- */

          description:

            `Order ${

              paymentOrder.order_number ||

              orderId

            }`,

          /* ---------------------------------------------

             RAZORPAY ORDER

          \--------------------------------------------- */

          order_id:

            paymentOrder.razorpay_order_id,

          /* =================================================

             THEME 9 RAZORPAY COLOR

          \================================================= */

          theme: {

            color:

              "#2d7552",

          },

          /* =================================================

             SUCCESS HANDLER

          \================================================= */

          handler:

            async function (

              response

            ) {

              try {

                setLoading(true);

                setPaymentError("");

                /* =========================================

                   VERIFY PAYMENT WITH BACKEND

                \========================================= */

                const verificationData = {

                  order_id:

                    orderId,

                  razorpay_order_id:

                    response.razorpay_order_id,

                  razorpay_payment_id:

                    response.razorpay_payment_id,

                  razorpay_signature:

                    response.razorpay_signature,

                };

                const verificationResponse =

                  await dispatch(

                    verifyRazorpayPayment(

                      restaurantId,

                      verificationData,
                    sessionToken

                    )

                  );

                console.log(

                  "RAZORPAY VERIFY RESPONSE:",

                  verificationResponse

                );

                /* =========================================

                   CLEAR CART ONLY AFTER SUCCESS

                \========================================= */

                clearCart();

                /* =========================================

                   SUCCESS SCREEN

                \========================================= */

                navigate(

                  `${homePath}/success`,

                  {

                    state: {

                      orderId,

                      orderNumber:

                        paymentOrder.order_number,

                      paymentMethod:

                        "ONLINE",

                      total:

                        Number(total),

                      paymentId:

                        response.razorpay_payment_id,

                    },

                  }

                );

              } catch (error) {

                console.error(

                  "Payment verification error:",

                  error

                );

                setPaymentError(

                  error?.response?.data?.detail ||

                  error?.message ||

                  "Payment verification failed. Please contact the restaurant."

                );

              } finally {

                setLoading(false);

              }

            },

          /* =================================================

             RAZORPAY MODAL

          \================================================= */

          modal: {

            ondismiss:

              function () {

                setLoading(false);

              },

            escape:

              true,

            backdropclose:

              false,

          },

          /* =================================================

             RETRY

          \================================================= */

          retry: {

            enabled:

              true,

            max_count:

              3,

          },

        };

        /* =================================================

           CHECK KEY

        \================================================= */

        if (!options.key) {

          throw new Error(

            "Razorpay key was not returned by the server."

          );

        }

        /* =================================================

           OPEN RAZORPAY

        \================================================= */

        const razorpay =

          new window.Razorpay(

            options

          );

        /* =================================================

           PAYMENT FAILED

        \================================================= */

        razorpay.on(

          "payment.failed",

          function (

            response

          ) {

            console.error(

              "RAZORPAY PAYMENT FAILED:",

              response

            );

            setPaymentError(

              response?.error?.description ||

              "Payment was unsuccessful. Please try again."

            );

            setLoading(false);

          }

        );

        razorpay.open();

      } catch (error) {

        console.error(

          "Payment error:",

          error

        );

        setPaymentError(

          error?.response?.data?.detail ||

          error?.message ||

          "Unable to start payment."

        );

        setLoading(false);

      }

    };

  /* =====================================================

     BACK TO ORDER

  \===================================================== */

  const handleBack = () => {

    navigate(

      `${homePath}/order`,

      {

        state: {

          subtotal:

            Number(subtotal),

          tax:

            Number(tax),

          serviceCharge:

            Number(serviceCharge),

          total:

            Number(total),

        },

      }

    );

  };

  /* =====================================================

     RENDER

  \===================================================== */

  return (

    <main className="theme9-payment-page">

      {/* =================================================

          HEADER

      \================================================= */}

      <section className="theme9-payment-header">

        <div className="theme9-payment-container">

          {/* BACK */}

          <button

            type="button"

            className="theme9-payment-back"

            onClick={handleBack}

            disabled={loading}

          >

            <ArrowLeft

              size={16}

            />

            Back to Order

          </button>

          {/* TITLE ROW */}

          <div className="theme9-payment-title-row">

            <div>

              <span className="theme9-payment-label">

                SECURE PAYMENT

              </span>

              <h1>

                Complete Payment

              </h1>

              <p>

                Finish your order with a

                fast and secure online

                payment.

              </p>

            </div>

            {/* TABLE */}

            <div className="theme9-payment-table">

              <span>

                TABLE

              </span>

              <strong>

                {tableId}

              </strong>

            </div>

          </div>

        </div>

      </section>

      {/* =================================================

          CONTENT

      \================================================= */}

      <section className="theme9-payment-content">

        <div className="theme9-payment-container">

          <div className="theme9-payment-layout">

            {/* =================================================

                PAYMENT MAIN

            \================================================= */}

            <div className="theme9-payment-main">

              {/* =================================================

                  PAYMENT INTRO

              \================================================= */}

              <div className="theme9-payment-intro">

                <span className="theme9-payment-step">

                  01

                </span>

                <div>

                  <span>

                    ONLINE PAYMENT

                  </span>

                  <h2>

                    Pay securely

                  </h2>

                  <p>

                    Complete your payment

                    to confirm your order.

                  </p>

                </div>

              </div>

              {/* =================================================

                  RAZORPAY BRAND CARD

              \================================================= */}

              <div className="theme9-payment-brand-card">

                <div className="theme9-payment-brand-icon">

                  <span>

                    ₹

                  </span>

                </div>

                <div className="theme9-payment-brand-info">

                  <span>

                    POWERED BY

                  </span>

                  <h2>

                    Razorpay

                  </h2>

                  <p>

                    Secure online payment

                    for your restaurant

                    order.

                  </p>

                </div>

                <div className="theme9-payment-brand-status">

                  <CheckCircle2

                    size={18}

                  />

                  <span>

                    READY

                  </span>

                </div>

              </div>

              {/* =================================================

                  PAYMENT METHODS INFO

              \================================================= */}

              <div className="theme9-payment-methods">

                <div className="theme9-payment-method">

                  <span>

                    <CreditCard

                      size={18}

                    />

                  </span>

                  <div>

                    <strong>

                      Cards

                    </strong>

                    <p>

                      Credit and debit

                      cards

                    </p>

                  </div>

                </div>

                <div className="theme9-payment-method">

                  <span>

                    <Smartphone

                      size={18}

                    />

                  </span>

                  <div>

                    <strong>

                      UPI & More

                    </strong>

                    <p>

                      Available options

                      appear in Razorpay

                    </p>

                  </div>

                </div>

                <div className="theme9-payment-method">

                  <span>

                    <ShieldCheck

                      size={18}

                    />

                  </span>

                  <div>

                    <strong>

                      Protected

                    </strong>

                    <p>

                      Secure payment

                      processing

                    </p>

                  </div>

                </div>

              </div>

              {/* =================================================

                  SECURITY

              \================================================= */}

              <div className="theme9-payment-security">

                <div className="theme9-payment-security-icon">

                  <LockKeyhole

                    size={21}

                  />

                </div>

                <div>

                  <span>

                    PAYMENT SECURITY

                  </span>

                  <strong>

                    Secure Payment

                  </strong>

                  <p>

                    Your payment is

                    securely processed

                    through Razorpay.

                    We never store your

                    card details.

                  </p>

                </div>

              </div>

              {/* =================================================

                  ORDER REFERENCE

              \================================================= */}

              <div className="theme9-payment-reference">

                <div className="theme9-payment-reference-icon">

                  <ShoppingBag

                    size={17}

                  />

                </div>

                <div>

                  <span>

                    ORDER REFERENCE

                  </span>

                  <strong>

                    Order #{orderId || "—"}

                  </strong>

                </div>

                <div className="theme9-payment-reference-table">

                  <span>

                    TABLE

                  </span>

                  <strong>

                    {tableId}

                  </strong>

                </div>

              </div>

              {/* =================================================

                  ERROR

              \================================================= */}

              {paymentError && (

                <div className="theme9-payment-error">

                  <strong>

                    Payment unsuccessful

                  </strong>

                  <p>

                    {paymentError}

                  </p>

                </div>

              )}

              {/* =================================================

                  PAY NOW

              \================================================= */}

              <button

                type="button"

                className="theme9-pay-now-button"

                onClick={

                  handlePayment

                }

                disabled={

                  loading ||
                  !sessionToken ||

                  !orderId

                }

              >

                {loading ? (

                  <>

                    <span className="theme9-payment-spinner" />

                    Opening Secure Payment...

                  </>

                ) : (

                  <>

                    <span>

                      Pay ₹{formatPrice(total)}

                    </span>

                    <ArrowRight

                      size={18}

                    />

                  </>

                )}

              </button>

              {/* =================================================

                  BOTTOM NOTE

              \================================================= */}

              <div className="theme9-payment-bottom-note">

                <LockKeyhole

                  size={13}

                />

                <span>

                  Secure payment powered by

                  Razorpay

                </span>

              </div>

            </div>

            {/* =================================================

                ORDER SUMMARY

            \================================================= */}

            <aside className="theme9-payment-summary">

              {/* SUMMARY TOP */}

              <div className="theme9-payment-summary-top">

                <div className="theme9-payment-summary-icon">

                  <ShoppingBag

                    size={19}

                  />

                </div>

                <div>

                  <span className="theme9-payment-summary-label">

                    02 · ORDER SUMMARY

                  </span>

                  <h2>

                    Order #{orderId || "—"}

                  </h2>

                </div>

              </div>

              <p className="theme9-payment-summary-subtitle">

                Final amount for your

                table order.

              </p>

              {/* =================================================

                  SUBTOTAL

              \================================================= */}

              <div className="theme9-payment-price-row">

                <span>

                  Subtotal

                </span>

                <strong>

                  ₹

                  {formatPrice(

                    subtotal

                  )}

                </strong>

              </div>

              {/* =================================================

                  TAX

              \================================================= */}

              <div className="theme9-payment-price-row">

                <span>

                  Tax

                </span>

                <strong>

                  ₹

                  {formatPrice(

                    tax

                  )}

                </strong>

              </div>

              {/* =================================================

                  SERVICE CHARGE

              \================================================= */}

              <div className="theme9-payment-price-row">

                <span>

                  Service Charge

                </span>

                <strong>

                  ₹

                  {formatPrice(

                    serviceCharge

                  )}

                </strong>

              </div>

              <div className="theme9-payment-divider" />

              {/* =================================================

                  TOTAL

              \================================================= */}

              <div className="theme9-payment-total">

                <div>

                  <span>

                    TOTAL

                  </span>

                  <small>

                    Amount to be paid

                  </small>

                </div>

                <strong>

                  ₹

                  {formatPrice(

                    total

                  )}

                </strong>

              </div>

              {/* =================================================

                  TABLE INFO

              \================================================= */}

              <div className="theme9-payment-order-info">

                <div>

                  <span>

                    TABLE

                  </span>

                  <strong>

                    {tableId}

                  </strong>

                </div>

                <div className="theme9-payment-order-status">

                  <CheckCircle2

                    size={14}

                  />

                  <span>

                    Order created

                  </span>

                </div>

              </div>

              {/* =================================================

                  SUMMARY SECURITY

              \================================================= */}

              <div className="theme9-payment-summary-security">

                <ShieldCheck

                  size={15}

                />

                <p>

                  Payment amount is created

                  and verified by the

                  server.

                </p>

              </div>

              {/* =================================================

                  FRESH NOTE

              \================================================= */}

              <div className="theme9-payment-fresh-note">

                <Leaf

                  size={14}

                />

                <span>

                  Your meal will be prepared

                  after payment confirmation.

                </span>

              </div>

            </aside>

          </div>

        </div>

      </section>

    </main>

  );

}

export default PaymentScreen;